// Base DAO for common data access patterns (Browser/Node.js compatible)
class BaseDAO {
  constructor() {
    this.cache = new Map();
    this.cacheExpiry = new Map();
    this.cacheTTL = 5 * 60 * 1000; // 5 minutes default
    
    // Session-level cache (persists for entire session)
    this.sessionCache = new Map();
    this.sessionCacheTTL = 30 * 60 * 1000; // 30 minutes for session cache

    // In-flight request dedup: concurrent callers for the same key share one fetch
    this.inflight = new Map();
    
    // Environment detection
    this.isNode = typeof window === 'undefined' && typeof process !== 'undefined';
    this.isBrowser = typeof window !== 'undefined';
  }

  // Generic cache management
  getCached(key) {
    if (this.cache.has(key)) {
      const expiry = this.cacheExpiry.get(key);
      if (expiry && Date.now() > expiry) {
        this.cache.delete(key);
        this.cacheExpiry.delete(key);
        return null;
      }
      return this.cache.get(key);
    }
    return null;
  }

  setCached(key, data, ttl = this.cacheTTL) {
    this.cache.set(key, data);
    this.cacheExpiry.set(key, Date.now() + ttl);
  }

  clearCache(pattern = null) {
    if (pattern) {
      // Clear cache entries matching pattern
      for (const key of this.cache.keys()) {
        if (key.includes(pattern)) {
          this.cache.delete(key);
          this.cacheExpiry.delete(key);
        }
      }
    } else {
      this.cache.clear();
      this.cacheExpiry.clear();
    }
  }

  // Session cache management
  getSessionCached(key) {
    if (this.sessionCache.has(key)) {
      const entry = this.sessionCache.get(key);
      if (Date.now() < entry.expiry) {
        return entry.data;
      }
      this.sessionCache.delete(key);
    }
    return null;
  }

  setSessionCached(key, data, ttl = this.sessionCacheTTL) {
    this.sessionCache.set(key, {
      data,
      expiry: Date.now() + ttl
    });
  }

  // Generic fetch with cache (checks session cache first)
  async fetchWithCache(url, cacheKey, ttl = this.cacheTTL, useSessionCache = true) {
    // Check session cache first for longer persistence
    if (useSessionCache) {
      const sessionCached = this.getSessionCached(cacheKey);
      if (sessionCached) return sessionCached;
    }
    
    // Then check regular cache
    const cached = this.getCached(cacheKey);
    if (cached) return cached;

    // Dedup concurrent callers (e.g. React StrictMode double-invoke, parallel widgets):
    // they share a single in-flight request instead of each firing their own.
    if (this.inflight.has(cacheKey)) {
      return this.inflight.get(cacheKey);
    }

    const request = (async () => {
      // Use fetch API (designed for browser/server HTTP context)
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      // Store in both caches
      this.setCached(cacheKey, data, ttl);
      if (useSessionCache) {
        this.setSessionCached(cacheKey, data);
      }

      return data;
    })();

    this.inflight.set(cacheKey, request);
    try {
      return await request;
    } catch (error) {
      console.error(`Failed to fetch ${url}:`, error);
      throw error;
    } finally {
      // Clear so failed requests can be retried later
      this.inflight.delete(cacheKey);
    }
  }

  // Batch fetch optimization
  async batchFetch(requests) {
    const promises = requests.map(({ url, cacheKey, ttl }) => 
      this.fetchWithCache(url, cacheKey, ttl)
    );
    
    const results = await Promise.allSettled(promises);
    return results.map((result, index) => ({
      request: requests[index],
      success: result.status === 'fulfilled',
      data: result.status === 'fulfilled' ? result.value : null,
      error: result.status === 'rejected' ? result.reason : null
    }));
  }

  // Date utilities
  isValidDate(dateString) {
    if (!dateString || typeof dateString !== 'string') return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime()) && dateString.match(/^\d{4}-\d{2}-\d{2}$/);
  }

  shouldShow(publishDate, publishStatus, showFuture = false) {
    if (publishStatus === 'draft' && !showFuture) return false;
    if (!publishDate) return true;
    if (!this.isValidDate(publishDate)) return true;
    return showFuture || new Date(publishDate) <= new Date();
  }
}

module.exports = { BaseDAO };